import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { marketingPersonApi } from '../../../modules/services/marketingPersonService';
import { useAppSelector } from '../../../modules/store/hooks';
import translate from '../../../i18n/translate';

interface OrganizerCommentsModalProps {
  open: boolean;
  onClose: () => void;
  /** Fired after a comment is added or deleted, so the parent can refresh counts. */
  onChanged?: () => void;
  assignmentId: number | null;
  organizerName?: string;
}

const OrganizerCommentsModal: React.FC<OrganizerCommentsModalProps> = ({ open, onClose, onChanged, assignmentId, organizerName }) => {
  const intl = useIntl();
  const language = useAppSelector((state) => state.auth.language);
  const lang = language?.split('-')?.[0] || 'en';

  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const { data: comments = [], isFetching, refetch } = marketingPersonApi.useGetOrganizerCommentsQuery(
    { assignmentId: assignmentId ?? 0, language: lang },
    { skip: !open || !assignmentId, refetchOnMountOrArgChange: true },
  );
  const [addComment, { isLoading: isAdding }] = marketingPersonApi.useAddOrganizerCommentMutation();
  const [deleteComment] = marketingPersonApi.useDeleteOrganizerCommentMutation();

  const handleAdd = async () => {
    if (!assignmentId || !text.trim()) return;
    setError('');
    try {
      await addComment({ assignmentId, text: text.trim(), language: lang }).unwrap();
      setText('');
      refetch();
      onChanged?.();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Failed to add comment');
    }
  };

  const handleDelete = async (commentId: number) => {
    setError('');
    try {
      await deleteComment({ commentId, language: lang }).unwrap();
      setDeleteConfirmId(null);
      refetch();
      onChanged?.();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Failed to delete comment');
    }
  };

  const handleClose = () => {
    setText('');
    setError('');
    setDeleteConfirmId(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle color="warning.main">
        {translate('marketingPersonsCommentsTitle')}{organizerName ? `: ${organizerName}` : ''}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Stack direction="row" gap={1} alignItems="flex-start">
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            color="warning"
            placeholder={intl.formatMessage({ id: 'marketingPersonsCommentPlaceholder' })}
            value={text}
            inputProps={{ maxLength: 2000 }}
            onChange={(e) => setText(e.target.value)}
          />
          <Button
            variant="contained"
            color="warning"
            disabled={isAdding || !text.trim()}
            onClick={handleAdd}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {translate('marketingPersonsCommentAdd')}
          </Button>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        {isFetching && comments.length === 0 ? (
          <CircularProgress color="warning" sx={{ alignSelf: 'center' }} />
        ) : comments.length === 0 ? (
          <Typography color="textSecondary" textAlign="center">{translate('marketingPersonsCommentsEmpty')}</Typography>
        ) : (
          <Stack gap={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
            {comments.map((c) => (
              <Box key={c.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="textSecondary">
                    {dayjs(c.createDate).format('DD.MM.YYYY HH:mm')}
                    {(c.createdByName || c.createdByPhone) && ` · ${c.createdByName || c.createdByPhone}`}
                  </Typography>
                  {deleteConfirmId === c.id ? (
                    <Stack direction="row" gap={0.5} alignItems="center">
                      <Typography variant="caption">{translate('marketingPersonsCommentDeleteConfirm')}</Typography>
                      <Button size="small" color="error" onClick={() => handleDelete(c.id)}>{translate('marketingPersonsDelete')}</Button>
                      <Button size="small" color="warning" onClick={() => setDeleteConfirmId(null)}>{translate('marketingPersonsCancel')}</Button>
                    </Stack>
                  ) : (
                    <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(c.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.text}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="warning">{translate('marketingPersonsClose')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizerCommentsModal;
